import { Play, Phone, Mail, Instagram, Facebook, X, Rocket, Calculator } from "lucide-react";
import { motion, useScroll, useTransform, useMotionValue, useSpring, useMotionTemplate, animate, AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { trackConversion } from "@/lib/consent";

// Static seed positions for the 14 ambient cyan particles in the hero
// background. Values are pre-computed so the layout stays consistent
// between renders (no jitter, no hydration mismatch).
const HERO_PARTICLES = [
  { left: "8%",  top: "22%", size: 6 },
  { left: "18%", top: "68%", size: 4 },
  { left: "30%", top: "38%", size: 3 },
  { left: "42%", top: "78%", size: 5 },
  { left: "55%", top: "18%", size: 4 },
  { left: "63%", top: "58%", size: 7 },
  { left: "72%", top: "30%", size: 3 },
  { left: "80%", top: "72%", size: 5 },
  { left: "88%", top: "44%", size: 4 },
  { left: "26%", top: "14%", size: 3 },
  { left: "48%", top: "50%", size: 2 },
  { left: "94%", top: "20%", size: 3 },
  { left: "12%", top: "48%", size: 4 },
  { left: "68%", top: "86%", size: 3 },
];

// Ambient marketing-vocabulary words that float in the hero background .
// depth simulated via scale + blur + opacity, so words appear to drift
// forward and backward in space. `mobile: false` hides the word on
// small screens (< sm) so nothing overlaps on phones.
// 8 statt 12. bewusst luftig, damit der Hero atmet.
const HERO_KEYWORDS = [
  { text: "ROAS",           left: "8%",  top: "16%", size: 42, dur: 12, delay: 0.0, mobile: true  },
  { text: "Zielgruppe",     left: "72%", top: "14%", size: 34, dur: 14, delay: 0.2, mobile: true  },
  { text: "Positionierung", left: "50%", top: "40%", size: 40, dur: 16, delay: 0.4, mobile: false },
  { text: "Reichweite",     left: "82%", top: "30%", size: 30, dur: 13, delay: 0.1, mobile: true  },
  { text: "Storytelling",   left: "10%", top: "34%", size: 36, dur: 15, delay: 0.3, mobile: false },
  { text: "Marke",          left: "4%",  top: "24%", size: 52, dur: 17, delay: 0.5, mobile: true  },
  { text: "Conversion",     left: "78%", top: "22%", size: 28, dur: 12, delay: 0.6, mobile: false },
  { text: "Sichtbarkeit",   left: "30%", top: "18%", size: 32, dur: 16, delay: 0.25, mobile: false },
];

// Reel Clips die als transparenter Hintergrund im unteren Hero Bereich
// scrollen.  Da wir jetzt nur eine Zeile haben (max 12 Video Elemente),
// funktioniert Autoplay stabil ohne Browser Throttling.  Videos sind
// muted + playsInline, damit Autoplay Policies in allen Browsern greifen.
const HERO_REEL_CLIPS = [
  "/media/reel/n1.mp4",
  "/media/reel/n3.mp4",
  "/media/reel/n5.mp4",
  "/media/reel/n7.mp4",
  "/media/reel/n9.mp4",
  "/media/reel/n10.mp4",
];

const HeroLine = ({ children }) => (
  <span className="block">{children}</span>
);

const Word = ({ children, delay = 0, from = "left", scrollX, scrollFilter, size = 1 }) => (
  <motion.span
    className="inline-block overflow-hidden align-bottom"
    style={{ paddingBottom: "0.1em", x: scrollX, filter: scrollFilter, willChange: "transform, filter", fontSize: `${size}em`, lineHeight: 1 }}
  >
    <motion.span
      className="inline-block hero-silver"
      initial={{ x: from === "left" ? "-110%" : "110%", opacity: 0, filter: "blur(8px)" }}
      animate={{ x: "0%", opacity: 1, filter: "blur(0px)" }}
      transition={{
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1],
        delay,
      }}
      style={{ willChange: "transform, filter" }}
    >
      {children}
      {"\u00A0"}
    </motion.span>
  </motion.span>
);

const CreateWord = ({ scrollX, scrollFilter }) => {
  const ref = useRef(null);
  const mx = useMotionValue(50); // % within the word
  const my = useMotionValue(50);
  const sx = useSpring(mx, { stiffness: 120, damping: 22, mass: 0.4 });
  const sy = useSpring(my, { stiffness: 120, damping: 22, mass: 0.4 });

  useEffect(() => {
    const onMove = (e) => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const px = ((e.clientX - rect.left) / rect.width) * 100;
      const py = ((e.clientY - rect.top) / rect.height) * 100;
      // Clamp to a slightly wider range so the spot can leave the word edges
      mx.set(Math.max(-20, Math.min(120, px)));
      my.set(Math.max(-40, Math.min(140, py)));
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [mx, my]);

  // Radial gradient, bright cyan glow following cursor (dark theme).
  const bgImage = useMotionTemplate`radial-gradient(circle at ${sx}% ${sy}%, #FFFFFF 0%, #C4F3F6 12%, #3FD3DB 40%, #2A9AA0 100%)`;

  // Ambient backdrop glow behind the word, a soft cyan light source that
  // follows the cursor and bleeds outside the text bounds.
  const backdrop = useMotionTemplate`radial-gradient(ellipse 70% 120% at ${sx}% ${sy}%, rgba(63,211,219,0.55) 0%, rgba(63,211,219,0.25) 25%, rgba(63,211,219,0.08) 55%, rgba(63,211,219,0) 75%)`;

  // Ambient drop-shadow that also tracks the cursor x/y but with looser range.
  const shadowX = useTransform(sx, [0, 100], [-40, 40]);
  const shadowY = useTransform(sy, [0, 100], [-25, 25]);
  const shadow = useMotionTemplate`drop-shadow(${shadowX}px ${shadowY}px 30px rgba(63,211,219,0.55)) drop-shadow(${shadowX}px ${shadowY}px 80px rgba(63,211,219,0.30)) drop-shadow(0 0 140px rgba(63,211,219,0.15))`;

  return (
    <motion.span
      ref={ref}
      className="inline-block align-bottom relative"
      style={{ overflow: "visible", x: scrollX, filter: scrollFilter, willChange: "transform, filter" }}
    >
      {/* Ambient backdrop glow */}
      <motion.span
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          top: "-40%",
          bottom: "-40%",
          left: "-15%",
          right: "-15%",
          backgroundImage: backdrop,
          zIndex: 0,
        }}
        animate={{
          opacity: [0.9, 0.55, 0.95, 0.7, 0.9],
          scale: [1, 1.035, 0.99, 1.02, 1],
          filter: [
            "blur(24px)",
            "blur(32px)",
            "blur(22px)",
            "blur(30px)",
            "blur(24px)",
          ],
        }}
        transition={{
          duration: 5.2,
          ease: "easeInOut",
          repeat: Infinity,
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
      />

      <motion.span
        className="inline-block relative"
        style={{ zIndex: 1, fontSize: "1.55em", lineHeight: 1 }}
        initial={{ x: "110%", opacity: 0, filter: "blur(10px)" }}
        animate={{ x: "0%", opacity: 1, filter: "blur(0px)" }}
        transition={{ duration: 1.7, ease: [0.16, 1, 0.3, 1], delay: 0.32 }}
      >
        <motion.span
          className="inline-block"
          style={{
            backgroundImage: bgImage,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            color: "transparent",
            filter: shadow,
          }}
          animate={{ opacity: [1, 0.88, 1] }}
          transition={{ duration: 2.8, ease: "easeInOut", repeat: Infinity }}
        >
          CREATE
        </motion.span>
        {"\u00A0"}
      </motion.span>
    </motion.span>
  );
};

export default function Hero() {
  const ref = useRef(null);
  const [contactOpen, setContactOpen] = useState(false);
  // Mobile Erkennung damit wir scroll driven Effekte auf schwachen
  // Geräten deaktivieren.  Verhindert das jitter Verhalten das der User
  // beim Scrollen gemeldet hat (viele scroll listeners + blur filter +
  // horizontal drift = ruckelt auf Handy).
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const upd = () => setIsMobile(mq.matches);
    upd();
    mq.addEventListener("change", upd);
    return () => mq.removeEventListener("change", upd);
  }, []);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  // Parallax y nur auf Desktop, sonst 0% damit auf Mobile nichts scrollruckelt
  const contentYraw = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const contentY = isMobile ? "0%" : contentYraw;
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0.2]);

  // Scroll-driven horizontal drift + light motion blur for the heading words.
  // Auf Mobile deaktivieren wir das aggressive blur + drift, weil der
  // combined Filter beim Scrollen auf Handy CPU frisst und ruckelt.
  const leftXraw = useTransform(scrollYProgress, [0, 0.55], [0, isMobile ? 0 : -240]);
  const rightXraw = useTransform(scrollYProgress, [0, 0.55], [0, isMobile ? 0 : 240]);
  const leftX = useSpring(leftXraw, { stiffness: 140, damping: 26, mass: 0.6 });
  const rightX = useSpring(rightXraw, { stiffness: 140, damping: 26, mass: 0.6 });
  const blurAmount = useTransform(scrollYProgress, [0, 0.55], [0, isMobile ? 0 : 6]);
  const blurFilter = useMotionTemplate`blur(${blurAmount}px)`;

  // Background video fades from 0.25 → 0 as the user scrolls through the hero.
  const scrollOpacity = useTransform(scrollYProgress, [0, 0.55], [0.25, 0]);

  // Force play der Hero Reel Videos.  Chrome & Safari blocken muted
  // Autoplay manchmal wenn viele Elemente parallel laden — mit einem
  // expliziten .play() Aufruf pro Video (mit catch für Policy Fehler)
  // starten die Loops zuverlässig sobald der DOM steht.
  useEffect(() => {
    const kick = () => {
      document
        .querySelectorAll('[data-testid="hero-video-marquee"] video')
        .forEach((v) => {
          v.muted = true;
          v.play().catch(() => {
            /* Autoplay geblockt — Video bleibt auf Poster stehen */
          });
        });
    };
    kick();
    // Nachziehen falls video Elemente per Lazy Boundary später mounten
    const t = setTimeout(kick, 500);
    return () => clearTimeout(t);
  }, []);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-svh w-full overflow-hidden bg-ink"
    >
      {/* Ambient background, anthrazit + floating cyan particles + soft
          central bloom + subtle white grid. Fades out slightly on scroll
          so the reel below feels like it takes over. */}
      <motion.div
        aria-hidden
        className="absolute top-0 left-0 right-0 h-svh z-0 bg-ink overflow-hidden pointer-events-none"
        style={{ opacity: useTransform(scrollOpacity, (v) => 0.6 + v * 1.6) }}
      >
        {/* particles, gentle float + twinkle */}
        {HERO_PARTICLES.map((p, i) => {
          const dur = 4 + (i % 5) * 0.8; // 4s. 7.2s per particle
          const drift = 8 + (i % 4) * 4; // 8. 20 px vertical drift
          const dx = (i % 3 - 1) * 6;    // -6, 0 or +6 px horizontal drift
          return (
            <motion.span
              key={i}
              className="absolute rounded-full bg-signal"
              style={{
                left: p.left,
                top: p.top,
                width: `${p.size}px`,
                height: `${p.size}px`,
                boxShadow: `0 0 ${p.size * 3}px ${p.size * 0.9}px rgba(63,211,219,0.55)`,
              }}
              animate={{
                y: [0, -drift, 0, drift * 0.6, 0],
                x: [0, dx, 0, -dx, 0],
                opacity: [0.12, 0.35, 0.18, 0.32, 0.12],
                scale: [1, 1.15, 0.95, 1.1, 1],
              }}
              transition={{
                duration: dur,
                repeat: Infinity,
                ease: "easeInOut",
                delay: (i * 0.35) % 3,
              }}
            />
          );
        })}
        {/* central cyan bloom */}
        <div
          className="absolute left-1/2 top-1/2 h-[520px] w-[820px] -translate-x-1/2 -translate-y-1/2"
          style={{
            background:
              "radial-gradient(closest-side, rgba(63,211,219,0.18) 0%, rgba(63,211,219,0.06) 40%, rgba(63,211,219,0) 72%)",
            filter: "blur(24px)",
          }}
        />

        {/* Ambient marketing keywords, drift forward/backward with blur+scale.
            Positioniert responsiv: fontSize clamped, damit sich auf Handy
            nichts überlappt. Manche Wörter sind auf Mobile ausgeblendet. */}
        <div aria-hidden className="absolute inset-0 overflow-hidden select-none pointer-events-none">
          {HERO_KEYWORDS.map((k, i) => (
            <motion.span
              key={k.text}
              className={`absolute font-display font-black uppercase tracking-tighter text-bone ${k.mobile ? "" : "hidden sm:block"}`}
              style={{
                left: k.left,
                top: k.top,
                fontSize: `clamp(${Math.round(k.size * 0.42)}px, ${(k.size * 100 / 1600).toFixed(2)}vw, ${k.size}px)`,
                whiteSpace: "nowrap",
                willChange: "transform, filter, opacity",
              }}
              initial={{ opacity: 0.06, scale: 0.85, filter: "blur(6px)" }}
              animate={{
                opacity: [0.10, 0.22, 0.13, 0.20, 0.10],
                scale: [0.9, 1.05, 1.12, 0.95, 0.9],
                filter: ["blur(4px)", "blur(1px)", "blur(2px)", "blur(1.5px)", "blur(4px)"],
                x: [(i % 2 === 0 ? -20 : 20), 0, (i % 2 === 0 ? 15 : -15)],
                y: [0, -10, 0, 6, 0],
              }}
              transition={{
                duration: k.dur,
                delay: k.delay,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              {k.text}
            </motion.span>
          ))}
        </div>
        {/* faint white grid overlay on anthrazit BG */}
        <div
          className="absolute inset-0"
          style={{
            opacity: 0.06,
            backgroundImage:
              "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
      </motion.div>

      {/* Hero Video Reel Hintergrund. Eine Zeile im unteren Bereich der
          Hero Section, liegt hinter dem "Ihre Marke, unsere Mission"
          Textblock plus den CTAs.  Oberkante mit sanftem Verlauf zum
          Anthracite Background damit die Kante nicht hart wirkt. */}
      <div
        aria-hidden
        data-testid="hero-video-marquee"
        className="absolute left-0 right-0 z-[1] overflow-hidden pointer-events-none"
        style={{
          bottom: 0,
          height: "clamp(200px, 34vh, 360px)",
          opacity: 0.5,
          maskImage:
            "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.35) 18%, rgba(0,0,0,0.85) 42%, rgba(0,0,0,1) 62%, rgba(0,0,0,0.85) 88%, rgba(0,0,0,0.55) 100%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.35) 18%, rgba(0,0,0,0.85) 42%, rgba(0,0,0,1) 62%, rgba(0,0,0,0.85) 88%, rgba(0,0,0,0.55) 100%)",
        }}
      >
        <div
          className="absolute inset-0 flex items-center overflow-hidden"
        >
          <div
            className="flex gap-5 shrink-0 items-stretch h-full py-3"
            style={{
              animation: "cre8-hero-reel-right 70s linear infinite",
              willChange: "transform",
            }}
          >
            {[...HERO_REEL_CLIPS, ...HERO_REEL_CLIPS].map((c, ci) => (
              <div
                key={ci}
                className="relative shrink-0 rounded-xl overflow-hidden border border-white/10 bg-black"
                style={{
                  width: "clamp(240px, 30vw, 440px)",
                  height: "100%",
                }}
              >
                <video
                  src={c}
                  poster={c.replace(".mp4", ".jpg")}
                  className="w-full h-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  style={{ filter: "saturate(1.1) contrast(1.05)" }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes cre8-hero-reel-right {
          from { transform: translateX(-50%); }
          to   { transform: translateX(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="hero-video-marquee"] > div > div { animation: none !important; }
        }
      `}</style>

      <motion.div
        style={{ y: contentY }}
        className="relative z-10 mx-auto max-w-[1600px] px-6 lg:px-12 pt-24 lg:pt-28 pb-64 lg:pb-80 min-h-svh flex flex-col justify-start gap-8 lg:gap-10"
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          className="flex items-center gap-3 text-[10px] sm:text-xs tracking-[0.2em] sm:tracking-[0.25em] text-bone/60"
        >
          <span className="h-px w-6 sm:w-10 bg-signal flex-shrink-0" />
          <span className="leading-snug uppercase">
            CRE8 <span className="normal-case">Studio GmbH · Medienagentur</span>
          </span>
        </motion.div>

        <div className="mt-8 lg:mt-12">
          <h1
            data-testid="hero-heading"
            className="font-display tracking-tight text-bone leading-[0.95] text-balance"
            style={{ fontSize: "clamp(2rem, 5.5vw, 4rem)" }}
          >
            <HeroLine>
              <Word delay={0.15} from="left" scrollX={leftX} scrollFilter={blurFilter} size={0.75}>WE</Word>
              <CreateWord scrollX={leftX} scrollFilter={blurFilter} />
            </HeroLine>
            <HeroLine>
              <Word delay={0.55} from="left" scrollX={leftX} scrollFilter={blurFilter} size={0.95}>VISUAL</Word>
              <Word delay={0.70} from="right" scrollX={rightX} scrollFilter={blurFilter} size={0.7}>IMPACT.</Word>
            </HeroLine>
          </h1>

          <motion.ul
            data-testid="hero-service-bullets"
            className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-3 max-w-6xl items-start"
          >
            {[
              "Positionierung",
              "Markenauftritt",
              "Website",
              "Medienproduktion",
              "Social Media",
              "Performance Ads",
            ].map((s, i) => (
              <motion.li
                key={s}
                initial={{ opacity: 0, y: 14, filter: "blur(6px)" }}
                animate={{
                  opacity: [0, 0.55, 0.55, 0.85],
                  y: [14, 8, 8, 0],
                  filter: [
                    "blur(6px) drop-shadow(0 0 0 rgba(63,211,219,0))",
                    "blur(3px) drop-shadow(0 0 6px rgba(63,211,219,0.25))",
                    "blur(3px) drop-shadow(0 0 8px rgba(63,211,219,0.35))",
                    "blur(0px) drop-shadow(0 0 14px rgba(63,211,219,0.30))",
                  ],
                }}
                transition={{
                  duration: 1.1,
                  times: [0, 0.4, 0.7, 1],
                  ease: ["easeOut", "linear", [0.2, 1, 0.2, 1]],
                  delay: 0.6 + i * 0.16,
                }}
                className="flex items-center gap-3 text-sm lg:text-base text-bone whitespace-nowrap"
              >
                <motion.span
                  initial={{ opacity: 0, scale: 0.2 }}
                  animate={{
                    opacity: [0, 1, 1, 1],
                    scale: [0.2, 2.8, 1.6, 1],
                    filter: [
                      "blur(8px)",
                      "blur(2px)",
                      "blur(1px)",
                      "blur(0px)",
                    ],
                    boxShadow: [
                      "0 0 0px rgba(63,211,219,0)",
                      "0 0 32px 8px rgba(63,211,219,1)",
                      "0 0 22px 4px rgba(63,211,219,0.8)",
                      "0 0 12px 2px rgba(63,211,219,0.6)",
                    ],
                  }}
                  transition={{
                    duration: 1.3,
                    times: [0, 0.35, 0.65, 1],
                    delay: 0.6 + i * 0.16 + 0.65,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  className="inline-block w-2 h-2 rounded-full bg-signal shrink-0"
                  style={{ willChange: "transform, filter, box-shadow" }}
                />
                {s}
              </motion.li>
            ))}
          </motion.ul>

          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.8 }}
            className="mt-12 w-full text-base lg:text-lg text-bone/80 leading-relaxed text-balance max-w-4xl"
          >
            <span className="block text-bone font-semibold text-2xl sm:text-3xl lg:text-4xl leading-tight mb-4">
              Ihre Marke, unsere Mission.
            </span>
            Wir machen Marken sichtbar. Von Branding bis Content. Alles aus
            einer Hand.
            <span className="mt-6 block text-sm lg:text-base text-bone/55 leading-relaxed font-normal">
              Für Selbstständige, Praxen, Handwerk und Mittelstand in Franken
              &amp; deutschlandweit, die planbar Neukunden gewinnen wollen.
            </span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.9 }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5"
          >
            <CyanCTAButton onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
            <BoneCTAButton />
          </motion.div>
        </div>
      </motion.div>

      <AnimatePresence>
        {contactOpen && <ContactPopup onClose={() => setContactOpen(false)} />}
      </AnimatePresence>
    </section>
  );
}

function CyanCTAButton({ onClick }) {
  return (
    <button
      type="button"
      data-testid="hero-contact-cta"
      onClick={onClick}
      className="btn-cyan-ghost inline-flex items-center gap-3 px-7 py-4 text-sm font-semibold rounded-full"
    >
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-signal/70 text-signal">
        <Rocket size={14} />
      </span>
      Jetzt durchstarten
    </button>
  );
}

function BoneCTAButton() {
  // Google-Ads-Konversion "Klick auf externen Link". feuert nur, wenn der
  // User Marketing-Cookies akzeptiert hat (via consent.js gesteuert).
  const onClick = () => {
    trackConversion("AW-18361166788/I4dWCJ695tocEMTXpLNE");
  };
  return (
    <a
      href="https://www.cre8-studio-rechner.de"
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      data-testid="hero-calculator-cta"
      aria-label="Kalkulationsrechner öffnen. Pakete und Preise ansehen"
      className="btn-bone-ghost inline-flex items-center gap-3 px-7 py-4 text-sm font-semibold rounded-full"
    >
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-bone/70 text-bone">
        <Calculator size={14} />
      </span>
      Erstelle dein Paket
    </a>
  );
}

function ContactPopup({ onClose }) {
  const items = [
    {
      icon: Phone,
      label: "Anrufen",
      sub: "+49 176 82067040",
      href: "tel:+4917682067040",
      testid: "contact-popup-phone",
    },
    {
      icon: Mail,
      label: "E Mail schreiben",
      sub: "Direkt hier über unser Formular",
      scrollTo: "#contact",
      testid: "contact-popup-email",
    },
    {
      icon: Instagram,
      label: "Instagram",
      sub: "@cre8studio.de",
      href: "https://www.instagram.com/cre8studio.de/",
      testid: "contact-popup-instagram",
    },
    {
      icon: Facebook,
      label: "Facebook",
      sub: "CRE8 Studio GmbH",
      href: "https://www.facebook.com/profile.php?id=61552161850823",
      testid: "contact-popup-facebook",
    },
  ];

  return (
    <motion.div
      data-testid="contact-popup"
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    >
      <motion.div
        onClick={onClose}
        className="absolute inset-0 bg-ink/80"
        initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
        animate={{ opacity: 1, backdropFilter: "blur(14px)" }}
        exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        style={{ WebkitBackdropFilter: "blur(14px)" }}
      />
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.9, filter: "blur(16px)" }}
        animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
        exit={{ opacity: 0, y: 24, scale: 0.94, filter: "blur(12px)" }}
        transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md bg-[#22232A] border border-white/10 p-8 lg:p-10 shadow-[0_30px_120px_-20px_rgba(0,0,0,0.9)]"
        style={{ willChange: "transform, filter, opacity" }}
      >
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-bone/50 mb-2">
              Hallo!
            </div>
            <h3 className="font-display font-bold text-bone text-2xl lg:text-3xl tracking-tight">
              Wie willst du uns erreichen?
            </h3>
          </div>
          <button
            type="button"
            data-testid="contact-popup-close"
            aria-label="Schließen"
            onClick={onClose}
            className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/15 text-bone/70 hover:border-signal hover:text-signal transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <ul className="space-y-3">
          {items.map((it, i) => {
            const Icon = it.icon;
            return (
              <motion.li
                key={it.label}
                initial={{ opacity: 0, x: -14, filter: "blur(6px)" }}
                animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                transition={{
                  duration: 0.7,
                  delay: 0.25 + i * 0.09,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                {it.scrollTo ? (
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      // Kurze Verzögerung, damit das Popup vor dem Scroll
                      // sauber ausblenden kann, sonst ruckelt der Übergang.
                      setTimeout(() => {
                        const target = document.querySelector(it.scrollTo);
                        if (target) {
                          target.scrollIntoView({ behavior: "smooth", block: "start" });
                        }
                      }, 280);
                    }}
                    data-testid={it.testid}
                    className="group w-full flex items-center justify-between p-4 border border-white/10 hover:border-signal hover:bg-[#2C2D33] transition-all text-left"
                  >
                    <span className="flex items-center gap-4">
                      <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-signal text-bone group-hover:bg-[#6EE7EF]">
                        <Icon size={16} />
                      </span>
                      <span className="text-left">
                        <span className="block text-sm font-semibold text-bone group-hover:text-signal transition-colors">
                          {it.label}
                        </span>
                        <span className="block text-xs text-bone/50">
                          {it.sub}
                        </span>
                      </span>
                    </span>
                  </button>
                ) : (
                  <a
                    href={it.href}
                    target={it.href.startsWith("http") ? "_blank" : undefined}
                    rel={it.href.startsWith("http") ? "noopener noreferrer" : undefined}
                    data-testid={it.testid}
                    className="group flex items-center justify-between p-4 border border-white/10 hover:border-signal hover:bg-[#2C2D33] transition-all"
                  >
                    <span className="flex items-center gap-4">
                      <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-signal text-bone group-hover:bg-[#6EE7EF]">
                        <Icon size={16} />
                      </span>
                      <span className="text-left">
                        <span className="block text-sm font-semibold text-bone group-hover:text-signal transition-colors">
                          {it.label}
                        </span>
                        <span className="block text-xs text-bone/50">
                          {it.sub}
                        </span>
                      </span>
                    </span>
                  </a>
                )}
              </motion.li>
            );
          })}
        </ul>
      </motion.div>
    </motion.div>
  );
}
